import {
  Archive,
  ArrowRight,
  Briefcase,
  Check,
  Clock,
  Ellipsis,
  RotateCcw,
  SkipForward,
  UserPlus,
  Users,
} from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { IconButton } from "@/components/ui/icon-button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import {
  ACTION_LABEL,
  ROLE_LABEL,
  STAGE_LABEL,
  STAGE_ORDER,
  type AssignmentAction,
  type AssignmentEvent,
  type Review,
  type ReviewStage,
  type Role,
  type UserId,
} from "../data/review-data"

/**
 * People & routing side sheet.
 *
 * The pipeline strip up top locates the review in the three-stage
 * backend workflow (initial → secondary → done). Archived is orthogonal
 * — a boolean that removes the review from working queues but preserves
 * the last stage — so it is surfaced as a separate badge, not a fourth
 * cell in the strip.
 *
 * Every hand-off appends one AssignmentEvent server-side. The History
 * timeline reads those verbatim so a finding is defensible six months
 * later: not just "moved to secondary" but "reassigned from Nadia to
 * Marisol · needs a formulation call".
 *
 * Archive and Skip-secondary sit inside a More menu because they are the
 * two actions with no cheap undo. Return-to-pool stays on the surface
 * because reversing it is just picking the review back up.
 */
export interface PeopleRoutingSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  review: Review
  colleagues: Array<{ userId: UserId; name: string; role: Role }>
  onAssign: (userId: UserId, name: string, role: Role, note: string | null) => void
  onReturnToPool: () => void
  onAdvance: () => void
  onSkipSecondPass: () => void
  onArchive: () => void
}

export function PeopleRoutingSheet({
  open,
  onOpenChange,
  review,
  colleagues,
  onAssign,
  onReturnToPool,
  onAdvance,
  onSkipSecondPass,
  onArchive,
}: PeopleRoutingSheetProps): JSX.Element {
  const [pickedId, setPickedId] = React.useState<UserId | "">("")
  const [note, setNote] = React.useState("")

  const picked = colleagues.find((c) => c.userId === pickedId) ?? null
  const currentIndex = STAGE_ORDER.indexOf(review.assignment.currentStage)
  const nextStage = STAGE_ORDER[currentIndex + 1] ?? null

  const submit = () => {
    if (!picked) return
    onAssign(picked.userId, picked.name, picked.role, note.trim() || null)
    setPickedId("")
    setNote("")
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        size="md"
        title="People & routing"
        description="Who is on this review, and where it goes next."
        maximizable
      >
        {/* Sheet body doesn't scroll — the History timeline scrolls inside
            its own section. Anything else that grows past the viewport (the
            side-docked variant on a short window) still gets a scrollbar
            from the SheetContent parent. */}
        <div className="@container flex h-full flex-col">
        <StageStrip
          currentIndex={currentIndex}
          archived={review.assignment.archived}
          secondarySkipped={review.assignment.secondarySkipped}
        />

        {/* Two column stacks at @2xl: short cards (Currently-with, Client)
            stack on the left, long cards (Hand-it-to, History) stack on the
            right. Splitting into two independent column stacks — instead of
            one grid across all four sections — is what stops the short left
            column from bottoming out with white space while History keeps
            going on the right. */}
        <div className="min-h-0 flex-1 @2xl:grid @2xl:grid-cols-2 @2xl:divide-x @2xl:divide-border-soft">
        <div className="flex flex-col divide-y divide-border-soft">
        <Section eyebrow="Currently with" icon={<Users className="size-3" aria-hidden="true" />}>
          {review.assignment.assignee ? (
            <AssigneeCard
              name={review.assignment.assignee.name}
              role={review.assignment.assignee.role}
              sinceISO={review.assignment.assignee.sinceISO}
            />
          ) : (
            <UnassignedCard />
          )}
        </Section>

        <Section eyebrow="Client" icon={<Briefcase className="size-3" aria-hidden="true" />}>
          {review.client ? (
            <div className="rounded-[12px] border border-border bg-surface p-3.5 shadow-e1">
              <div className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="grid size-9 shrink-0 place-items-center rounded-[8px] bg-print-tint font-id text-[13px] font-semibold uppercase text-print ring-1 ring-inset ring-print/15"
                >
                  {review.client.clientName.slice(0, 2)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body font-semibold text-text">
                    {review.client.clientName}
                  </p>
                  <p className="mt-0.5 text-caption text-text-3">
                    {review.client.memberCount}{" "}
                    {review.client.memberCount === 1 ? "person" : "people"} can see this review.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[12px] border border-dashed border-border bg-surface-sunken/50 p-3.5">
              <div className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="grid size-9 shrink-0 place-items-center rounded-[8px] border border-dashed border-border-strong bg-surface text-text-3"
                >
                  <Briefcase className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-body-sm font-semibold text-text">
                    No client attached
                  </p>
                  <p className="mt-0.5 text-caption leading-[1.5] text-text-3">
                    Internal work — findings stay with the review team. Attach an
                    engagement on the Clients screen when it should be shared.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Section>
        </div>

        <div className="flex min-h-0 flex-col divide-y divide-border-soft">
        <Section eyebrow="Hand it to" icon={<UserPlus className="size-3" aria-hidden="true" />}>
          <div className="rounded-[12px] border border-border bg-surface p-3.5 shadow-e1">
            <div className="grid gap-3">
              <FormField label="Colleague">
                <Select
                  value={pickedId}
                  onValueChange={(v) => setPickedId(v as UserId | "")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a colleague" />
                  </SelectTrigger>
                  <SelectContent>
                    {colleagues.map((c) => (
                      <SelectItem
                        key={c.userId}
                        value={c.userId}
                        description={ROLE_LABEL[c.role]}
                      >
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField
                label="Note"
                hint="Recorded on the trail — the next person reads this first."
              >
                <Textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.currentTarget.value)}
                  placeholder="Why it is moving. Optional."
                />
              </FormField>
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                {picked ? (
                  <Button
                    variant="primary"
                    size="sm"
                    leadingIcon={<UserPlus />}
                    onClick={submit}
                  >
                    Hand it to {picked.name.split(" ")[0]}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    trailingIcon={<ArrowRight />}
                    onClick={onAdvance}
                    disabled={!nextStage || review.assignment.archived}
                  >
                    {nextStage
                      ? `Advance to ${STAGE_LABEL[nextStage]}`
                      : "Already done"}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="default"
                  leadingIcon={<RotateCcw />}
                  onClick={onReturnToPool}
                  disabled={!review.assignment.assignee}
                >
                  Return to pool
                </Button>
                <span className="flex-1" />
                <MoreMenu
                  currentStage={review.assignment.currentStage}
                  onSkipSecondPass={onSkipSecondPass}
                  onArchive={onArchive}
                />
              </div>
            </div>
          </div>
        </Section>

        <Section
          eyebrow="History"
          // History fills whatever height remains in the right column and
          // scrolls its own list. Routing controls above stay put; the sheet
          // body itself doesn't scroll.
          className="min-h-0 flex-1"
          bodyClassName="overflow-y-auto pr-1"
        >
          {review.assignment.events.length === 0 ? (
            <p className="rounded-[10px] border border-dashed border-border-soft px-3 py-4 text-center text-body-sm text-text-3">
              Nothing recorded yet.
            </p>
          ) : (
            <ol className="relative space-y-0 pl-1">
              {review.assignment.events
                .slice()
                .reverse()
                .map((e, i, arr) => (
                  <TrailEntry
                    key={`${e.at}-${e.action}-${i}`}
                    event={e}
                    isFirst={i === 0}
                    isLast={i === arr.length - 1}
                  />
                ))}
            </ol>
          )}
        </Section>
        </div>
        </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Section container. Every section is separated by a hairline; the eyebrow
 * uses the mono id typeface for the small-caps feel the redesign relies on.
 */
function Section({
  eyebrow,
  icon,
  children,
  className,
  bodyClassName,
}: {
  eyebrow: string
  icon?: React.ReactNode
  children: React.ReactNode
  /** Extra classes on the <section>. Used to let History flex-fill the column. */
  className?: string
  /** Extra classes on the body wrapper. Used to let History scroll internally. */
  bodyClassName?: string
}): JSX.Element {
  return (
    <section
      className={cn(
        "flex flex-col border-b border-border-soft px-5 py-4 last:border-b-0",
        className,
      )}
    >
      <h3 className="mb-3 flex items-center gap-1.5 font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
        {icon && <span className="opacity-70">{icon}</span>}
        {eyebrow}
      </h3>
      <div className={cn("min-h-0", bodyClassName)}>{children}</div>
    </section>
  )
}

/**
 * The three-stage pipeline strip. Backend workflow is initial → secondary
 * → done; archived is a boolean, not a fourth stage, and renders as a
 * banner over the strip. "Secondary skipped" replaces the middle cell's
 * check with a skip glyph so the trail is legible from the strip alone.
 */
function StageStrip({
  currentIndex,
  archived,
  secondarySkipped,
}: {
  currentIndex: number
  archived: boolean
  secondarySkipped: boolean
}): JSX.Element {
  return (
    <div className="border-b border-border-soft bg-surface-sunken/30 px-5 py-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
          Pipeline
        </span>
        <span className="font-id text-[10px] tabular-nums text-text-3">
          Stage {currentIndex + 1} / {STAGE_ORDER.length}
        </span>
      </div>
      <ol
        aria-label="Review pipeline"
        className="grid overflow-hidden rounded-[10px] border border-border bg-surface shadow-e1"
        style={{ gridTemplateColumns: `repeat(${STAGE_ORDER.length}, minmax(0, 1fr))` }}
      >
        {STAGE_ORDER.map((s, i) => {
          const past = i < currentIndex
          const active = i === currentIndex
          const skipped = secondarySkipped && s === "secondary"
          return (
            <li
              key={s}
              aria-current={active ? "step" : undefined}
              className={cn(
                "relative min-w-0 border-r border-border-soft px-2 py-2.5 last:border-r-0 transition-colors",
                past && "bg-master-tint/60",
                active && "bg-surface shadow-[inset_0_-3px_0_var(--brand)]",
                skipped && "bg-print-tint/40",
              )}
            >
              <div className="flex items-center gap-1.5">
                {skipped ? (
                  <span
                    aria-hidden="true"
                    className="grid size-3 shrink-0 place-items-center rounded-full bg-print"
                  >
                    <SkipForward className="size-2 text-white" strokeWidth={3} />
                  </span>
                ) : past ? (
                  <span
                    aria-hidden="true"
                    className="grid size-3 shrink-0 place-items-center rounded-full bg-master"
                  >
                    <Check className="size-2 text-white" strokeWidth={3} />
                  </span>
                ) : active ? (
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-full bg-brand ring-2 ring-brand/20"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="size-2 shrink-0 rounded-full border border-border-strong"
                  />
                )}
                <span
                  className={cn(
                    "truncate text-[11px] font-semibold leading-none",
                    active && "text-text",
                    past && "text-master",
                    skipped && "text-print",
                    !active && !past && !skipped && "text-text-disabled",
                  )}
                >
                  {STAGE_LABEL[s]}
                </span>
              </div>
            </li>
          )
        })}
      </ol>
      {archived && (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-surface-sunken px-2.5 py-1 font-id text-[10px] font-medium uppercase tracking-[0.08em] text-text-2">
          <Archive className="size-3" aria-hidden="true" />
          Archived — hidden from the working queue
        </p>
      )}
    </div>
  )
}

/**
 * Assignee card. Role-tinted avatar with a live "since" timer so glancing
 * at the sheet answers "who and how long" without a second click.
 */
function AssigneeCard({
  name,
  role,
  sinceISO,
}: {
  name: string
  role: Role
  sinceISO: string
}): JSX.Element {
  return (
    <div className="rounded-[12px] border border-border bg-surface p-3.5 shadow-e1">
      <div className="flex items-center gap-3">
        <RoleAvatar role={role} name={name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body font-semibold text-text">{name}</p>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-caption text-text-3">
            <span>{ROLE_LABEL[role]}</span>
            <span aria-hidden="true" className="text-text-3/60">·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3" aria-hidden="true" />
              <RelativeTime iso={sinceISO} />
            </span>
          </p>
        </div>
      </div>
    </div>
  )
}

function UnassignedCard(): JSX.Element {
  return (
    <div className="rounded-[12px] border border-dashed border-border bg-surface-sunken/50 p-3.5">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="grid size-9 shrink-0 place-items-center rounded-full border border-dashed border-border-strong bg-surface"
        >
          <Users className="size-4 text-text-3" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-body font-semibold text-text">Unassigned</p>
          <p className="mt-0.5 text-caption text-text-3">
            Waiting in the pool — nobody has picked it up.
          </p>
        </div>
      </div>
    </div>
  )
}

const ROLE_BG: Record<Role, string> = {
  admin: "bg-brand",
  reviewer: "bg-info",
  sme: "bg-people",
  viewer: "bg-text-2",
  client: "bg-print",
}

/**
 * Role-tinted avatar. Uses two-letter initials for legibility at this size.
 * Colour maps to the backend Role enum so the same colour always means the
 * same role across the app — reviewer info-blue, sme people-violet, and so on.
 */
function RoleAvatar({ role, name }: { role: Role; name?: string }): JSX.Element {
  const initials = name
    ? name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join("")
        .toUpperCase()
    : role[0]?.toUpperCase()
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-full font-id text-[13px] font-semibold uppercase text-white ring-2 ring-surface",
        ROLE_BG[role],
      )}
    >
      {initials}
    </span>
  )
}

function MoreMenu({
  currentStage,
  onSkipSecondPass,
  onArchive,
}: {
  currentStage: ReviewStage
  onSkipSecondPass: () => void
  onArchive: () => void
}): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const canSkipSecondary = currentStage === "initial"
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton
          size="sm"
          variant="ghost"
          label="More routing actions"
          icon={<Ellipsis />}
        />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-64 p-1">
        <MenuItem
          icon={<SkipForward className="size-3.5" aria-hidden="true" />}
          label="Skip secondary review"
          hint="Goes straight to done. Recorded on the trail."
          disabled={!canSkipSecondary}
          onSelect={() => {
            setOpen(false)
            onSkipSecondPass()
          }}
        />
        <MenuItem
          icon={<Archive className="size-3.5" aria-hidden="true" />}
          label="Archive this review"
          hint="Hidden from the working queue. The trail is kept."
          destructive
          onSelect={() => {
            setOpen(false)
            onArchive()
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

function MenuItem({
  icon,
  label,
  hint,
  destructive,
  disabled,
  onSelect,
}: {
  icon: React.ReactNode
  label: string
  hint: string
  destructive?: boolean
  disabled?: boolean
  onSelect: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors duration-120 ease-out",
        destructive
          ? "text-err hover:bg-err-tint"
          : "text-text hover:bg-surface-sunken",
        "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      <span className="mt-[3px] shrink-0 opacity-80">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-body-sm font-medium leading-tight">{label}</span>
        <span className="mt-0.5 block text-micro leading-normal text-text-3">
          {hint}
        </span>
      </span>
    </button>
  )
}

const ACTION_DOT: Record<AssignmentAction, string> = {
  assign: "bg-info",
  reassign: "bg-info",
  throwback: "bg-text-3",
  advance_stage: "bg-master",
  skip_secondary: "bg-print",
  archive: "bg-text-2",
  unarchive: "bg-border-strong",
}

/**
 * One entry in the trail timeline.
 *
 * Reads the raw AssignmentEvent — action, from/to user, target stage,
 * note — and renders a single row. Every event is rich enough to answer
 * "what happened and why", not just "the stage moved".
 */
function TrailEntry({
  event,
  isFirst,
  isLast,
}: {
  event: AssignmentEvent
  isFirst: boolean
  isLast: boolean
}): JSX.Element {
  const title = summarize(event)
  const dot = ACTION_DOT[event.action]
  return (
    <li className="grid grid-cols-[18px_minmax(0,1fr)] gap-3">
      <span className="relative flex justify-center">
        {!isFirst && (
          <span aria-hidden="true" className="absolute top-0 h-[10px] w-px bg-border" />
        )}
        <span
          aria-hidden="true"
          className={cn(
            "mt-[10px] size-2.5 shrink-0 rounded-full ring-2 ring-surface",
            dot,
          )}
        />
        {!isLast && (
          <span
            aria-hidden="true"
            className="absolute bottom-0 top-[22px] w-px bg-border"
          />
        )}
      </span>
      <div className={cn("min-w-0 pb-4", isLast && "pb-0")}>
        <p className="text-body-sm leading-snug text-text">
          <span className="font-medium">{title}</span>
          {event.actor && <span className="text-text-3"> · {event.actor}</span>}
        </p>
        {event.note && (
          <p className="mt-1 rounded-[8px] border-l-2 border-border bg-surface-sunken/50 px-2.5 py-1.5 text-caption leading-normal text-text-2">
            {event.note}
          </p>
        )}
        <p className="mt-1 font-id text-[10.5px] tabular-nums text-text-3">
          {formatTrailDate(event.at)}
        </p>
      </div>
    </li>
  )
}

/**
 * Build the trail entry's title line from the event shape. The backend
 * gives us structured data (action + fromUser + toUser + toStage) — using
 * it means the user reads "Reassigned Nadia → Marisol" instead of the
 * flat "reassigned" a lazy renderer would produce.
 */
function summarize(e: AssignmentEvent): string {
  switch (e.action) {
    case "assign":
      return e.toUser ? `${ACTION_LABEL.assign} to ${e.toUser}` : ACTION_LABEL.assign
    case "reassign":
      if (e.fromUser && e.toUser) return `Reassigned ${e.fromUser} → ${e.toUser}`
      if (e.toUser) return `Reassigned to ${e.toUser}`
      return ACTION_LABEL.reassign
    case "throwback":
      return e.fromUser
        ? `Returned to pool from ${e.fromUser}`
        : ACTION_LABEL.throwback
    case "advance_stage":
      return e.toStage
        ? `Advanced to ${STAGE_LABEL[e.toStage]}`
        : ACTION_LABEL.advance_stage
    case "skip_secondary":
      return ACTION_LABEL.skip_secondary
    case "archive":
      return ACTION_LABEL.archive
    case "unarchive":
      return ACTION_LABEL.unarchive
  }
}

function formatTrailDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/** Live relative time — updates every minute while the sheet is open. */
function RelativeTime({ iso }: { iso: string }): JSX.Element {
  const [, tick] = React.useReducer((n: number) => n + 1, 0)
  React.useEffect(() => {
    const t = setInterval(tick, 60_000)
    return () => clearInterval(t)
  }, [])
  const then = new Date(iso).getTime()
  const now = Date.now()
  const mins = Math.max(0, Math.round((now - then) / 60_000))
  if (mins < 1) return <>just now</>
  if (mins < 60) return <>{mins}m</>
  const hours = Math.round(mins / 60)
  if (hours < 24) return <>{hours}h</>
  return <>{Math.round(hours / 24)}d</>
}
